import React, { Fragment } from 'react';
import styled, { css } from 'styled-components';
import usePrevious from 'shared/utils/usePrevious';
import { colors } from '@grnhse/seedling/lib/azalea/constants';
import Button from '@grnhse/seedling/lib/azalea/components/button';
// @ts-expect-error - TS7016 - Could not find a declaration file for module '@grnhse/seedling/lib/azalea/components/card_table'. 'node_modules/@grnhse/seedling/lib/azalea/components/card_table.js' implicitly has an 'any' type.
import { Row } from '@grnhse/seedling/lib/azalea/components/card_table';
import Toggle from '@grnhse/seedling/lib/azalea/components/toggle';
import type { DataCollectionRuleInterface } from './data_collection_rule';
import EditButton from '../shared/edit_button';
import { withLocalePrefix } from 'shared/utils/translation';
import ConfirmModal from '@grnhse/seedling/lib/azalea/components/modals/confirm_modal';
import WarningModal from '@grnhse/seedling/lib/azalea/components/modals/warning_modal';
import { DataCollectionRulesContext } from './context';
// @ts-expect-error - TS7016 - Could not find a declaration file for module '../shared/table_cell'. 'app/webpack/javascripts/gdpr/components/shared/table_cell.js' implicitly has an 'any' type.
import TableCell from '../shared/table_cell';
import { update } from '../../utils/request';
import EmailRecipients from './show/email_recipients';
import Offices from './show/offices';

type Props = {
  rule: DataCollectionRuleInterface;
  editing: boolean;
};

const t = withLocalePrefix('gdpr.data_collection_rules.view.table');

const EditableRow = styled(Row)`
  background-color: ${(props) =>
    props.editing ? colors.lightestGrey : 'initial'};
  color: ${(props) =>
    props.enabled || props.editing ? 'initial' : colors.mediumGrey};
`;

const EmailRecipientsTableCell = styled(TableCell)`
  ${(props) =>
    props.editing &&
    css`
      display: block;
      height: 13rem;
    `}
`;

export default function Rule(props: Props) {
  const { editing } = props;
  const { edited, meta, onCancel, onEdit, onDelete, onSave } = React.useContext(
    DataCollectionRulesContext
  );
  const [rule, setRule] = React.useState(props.rule);
  const previousEnabled = usePrevious(rule.enabled);

  React.useEffect(() => {
    if (previousEnabled !== rule.enabled) {
      (async () => {
        const response = await update(meta.update_rules_path, rule, {
          scope: 'data_collection_rule',
        });
        setRule(response.data);
      })();
    }
  }, [rule, previousEnabled, props.rule, meta.update_rules_path]);

  return (
    <Fragment>
      <EditableRow
        editing={editing}
        enabled={rule.enabled}
        data-provides={editing ? 'collection_rule_form' : 'collection_rule'}
      >
        <TableCell data-provides="toggle">
          {!editing && (
            <Toggle
              enabled={rule.enabled}
              id={`${rule.id}`}
              onClick={() => setRule({ ...rule, enabled: !rule.enabled })}
            />
          )}
        </TableCell>
        <TableCell data-provides="offices">
          <Offices
            editing={editing}
            rule={editing ? rule : props.rule}
            setRule={setRule}
          />
        </TableCell>
        <EmailRecipientsTableCell
          editing={editing}
          data-provides="email_recipients"
        >
          {/* @ts-expect-error - TS2786 - 'EmailRecipients' cannot be used as a JSX component. */}
          <EmailRecipients
            editing={editing}
            rule={editing ? rule : props.rule}
            setRule={setRule}
          />
        </EmailRecipientsTableCell>
        <TableCell data-provides="actions">
          {!editing && (
            <EditButton
              hideBg
              onClick={() => onEdit(rule)}
              data-provides="edit"
            >
              {t('rule.edit')}
            </EditButton>
          )}
        </TableCell>
      </EditableRow>
      {editing && (
        <EditableRow editing={editing}>
          <TableCell colSpan="2">
            {!!rule.id && (
              <WarningModal
                title={t('rule.delete_confirmation_title')}
                warningText={t('rule.delete_confirmation_text')}
                dangerText={t('rule.delete_confirmation_button_text')}
                submitText={t('rule.delete_confirmation_cancel_text')}
                maxWidth="md"
                onConfirm={() => onDelete(rule)}
              >
                <Button danger data-provides="delete">
                  {t('rule.delete')}
                </Button>
              </WarningModal>
            )}
          </TableCell>
          <TableCell colSpan="2">
            {edited ? (
              <ConfirmModal
                title={t('rule.cancel_confirmation_title')}
                confirmText={t('rule.cancel_confirmation_text')}
                submitText={t('rule.cancel_confirmation_button_text')}
                cancelText={t('rule.cancel_confirmation_cancel_text')}
                maxWidth="md"
                onConfirm={() => {
                  setRule(props.rule);
                  onCancel();
                }}
              >
                <Button hideBg data-provides="cancel">
                  {t('rule.cancel')}
                </Button>
              </ConfirmModal>
            ) : (
              <Button
                hideBg
                data-provides="cancel"
                onClick={() => {
                  setRule(props.rule);
                  onCancel();
                }}
              >
                {t('rule.cancel')}
              </Button>
            )}
            <Button fancy onClick={() => onSave(rule)} data-provides="save">
              {t('rule.save')}
            </Button>
          </TableCell>
        </EditableRow>
      )}
    </Fragment>
  );
}
